import classnames from 'clsx';
import React, { useCallback, useContext } from 'react';
import PropTypes from 'prop-types';

import { emptyArray, emptyObject } from 'ui-components/default-props/empty';
import { useContextTypes } from '../../hooks/use-context-types';
import { useGlobalKeyDown } from '../../hooks/use-global-key-down';
import { DisplayGridColumnPropType } from '../../prop-types/display-grid-column';
import { DisplayGridRecordPropType } from '../../prop-types/display-grid-record';
import { DisplayGridGroupRecordPropType } from '../../prop-types/display-grid-group-record';
import { ResizeObserverWrapper } from '../resize-observer-wrapper';

import { GridHelperService } from '../../services/grid-helper-service';
import { usePerformanceTracker } from '../../hooks/services/use-performance-tracker';
import { RowHoverSubject } from './observables/row-hover-subject';
import { OnKeyPressSubject } from './observables/on-key-press-subject';

import { useGridClassNameGenerator } from './hooks';
import { Context, Provider } from './context';
import { RowContextProvider } from './row-context';
import { RowOffsetSubject } from './observables/row-offset-subject';
import { BUFFER_ROWS } from './constants';

import { DisplayGridBody } from './display-grid-body';
import { DisplayGridHeader } from './display-grid-header';

import style from './index.module.css';

/**
 * @deprecated Use `DataGrid` from `@shipstation/client-common/components/data-grid` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export const DisplayGrid = () => {
  useContextTypes('DisplayGrid', Context, {
    data: PropTypes.arrayOf(PropTypes.oneOfType([DisplayGridRecordPropType, DisplayGridGroupRecordPropType])).isRequired,
    height: PropTypes.number,
    nonPinnedColumns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    onGridScrolled: PropTypes.func,
    pinnedColumns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
    placeholder: PropTypes.element,
    placeholderStyle: PropTypes.string,
    shouldForceShowPlaceholder: PropTypes.bool,
    ref: PropTypes.any,
    width: PropTypes.number,
  });

  const {
    data,
    height,
    nonPinnedColumns,
    pinnedColumns,
    placeholder,
    placeholderStyle,
    ref,
    width,
    resetToScrollTop,
    shouldForceShowPlaceholder,
  } = useContext(Context);

  const renderedCallback = useCallback(() => data.length > 0, [data]);
  usePerformanceTracker('hooks-display-grid', renderedCallback);

  const onRowHoverOut = useCallback(() => {
    RowHoverSubject.next(null);
  }, []);
  const className = useGridClassNameGenerator();

  const onKeyDown = useCallback((event) => {
    OnKeyPressSubject.next(event);
  }, []);

  useGlobalKeyDown(onKeyDown);

  const calculateRowOffset = useCallback((scrollTop) => {
    return Math.round(Math.floor(scrollTop / GridHelperService.getRowHeight()) / BUFFER_ROWS) * BUFFER_ROWS;
  }, []);

  // Grid scroll handler for left-right and up-down scrolling
  const onGridScrolled = useCallback(
    (event) => {
      const { scrollTop } = event.target;

      // If we're in the middle of a column header reorder, sticky positioning
      // necessitates us resetting the scrollTop constantly, due to a browser bug.
      if (resetToScrollTop !== null) {
        ref.current.scrollTop = resetToScrollTop;
      }

      // Save any rowOffset changes to the reducer state
      RowOffsetSubject.next(calculateRowOffset(resetToScrollTop || scrollTop));
    },
    [calculateRowOffset, resetToScrollTop, ref],
  );

  const showPlaceHolder = data.length === 0 || shouldForceShowPlaceholder;

  return (
    <div className={style['display-grid-overflow-wrapper']}>
      <div ref={ref} className={style['display-grid-wrapper']} onScroll={onGridScrolled}>
        <div className={classnames(style['display-grid'], className)} onMouseLeave={onRowHoverOut}>
          {height !== null && width !== null && data.length > 0 && (
            <React.Fragment>
              <div className={classnames(style['flex-row'], style.header)}>
                <DisplayGridHeader columns={pinnedColumns} pinned />
                <DisplayGridHeader columns={nonPinnedColumns} />
              </div>
              <div className={style['flex-row']}>
                <DisplayGridBody columns={pinnedColumns} pinned />
                <DisplayGridBody columns={nonPinnedColumns} />
              </div>
            </React.Fragment>
          )}

          {showPlaceHolder && (
            <div
              className={classnames(style['display-grid-placeholder'], {
                [placeholderStyle]: placeholderStyle,
              })}
            >
              {placeholder}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MemoDisplayGrid = React.memo(DisplayGrid);

/*
 * Some of the style generation and virtualization that needs to happen depends on
 * knowing the exact width/height that we have in our containing wrapper, so we use
 * a ResizeWrapper component to alert us any time a resize operation of any kid
 * causes the component's containing element to change dimensions. We subtract out
 * one row height from the height we're passing on, to account for the header row.
 */
/**
 * @deprecated Use `DataGrid` from `@shipstation/client-common/components/data-grid` instead.
 * See: ui-components/readme-documentation/MIGRATION.md
 */
export const HooksDisplayGrid = React.memo(
  ({
    columns,
    activeGroupByColumns = emptyArray,
    selected,
    onSelectedChanged,
    disableMultiSelect = false,
    renderCheckbox = true,
    getColumnId,
    onColumnsChanged,
    onOrderByChanged,
    onRowDoubleClick,
    placeholder,
    data = emptyArray,
    disabledIds = emptyObject,
    shouldForceShowPlaceholder = false,
    placeholderStyle,
  }) => (
    <ResizeObserverWrapper>
      {({ height, ref, width }) => (
        <Provider
          ref={ref}
          activeGroupByColumns={activeGroupByColumns}
          columns={columns}
          data={data}
          disableMultiSelect={disableMultiSelect}
          getColumnId={getColumnId}
          height={height - GridHelperService.getRowHeight()}
          onColumnsChanged={onColumnsChanged}
          onOrderByChanged={onOrderByChanged}
          onRowDoubleClick={onRowDoubleClick}
          placeholder={placeholder}
          placeholderStyle={placeholderStyle}
          renderCheckbox={renderCheckbox}
          shouldForceShowPlaceholder={shouldForceShowPlaceholder}
          width={width}
        >
          <RowContextProvider
            data={data}
            disabledIds={disabledIds}
            disableMultiSelect={disableMultiSelect}
            onSelectedChanged={onSelectedChanged}
            selected={selected}
          >
            <MemoDisplayGrid />
          </RowContextProvider>
        </Provider>
      )}
    </ResizeObserverWrapper>
  ),
);

HooksDisplayGrid.propTypes = {
  columns: PropTypes.arrayOf(DisplayGridColumnPropType).isRequired,
  activeGroupByColumns: PropTypes.array,
  selected: PropTypes.array,
  onSelectedChanged: PropTypes.func,
  disableMultiSelect: PropTypes.bool,
  getColumnId: PropTypes.func,
  onColumnsChanged: PropTypes.func,
  onOrderByChanged: PropTypes.func,
  onRowDoubleClick: PropTypes.func,
  placeholder: PropTypes.element,
  placeholderStyle: PropTypes.string,
  data: PropTypes.array,
  disabledIds: PropTypes.object,
  renderCheckbox: PropTypes.bool,
  shouldForceShowPlaceholder: PropTypes.bool,
};
